/**
 * Shaping YouTube Data API responses into the app's own {@link Track} form.
 *
 * There is no public "YouTube Music" API. What the Data API gives us is the
 * same Google account's playlists, liked videos and search — which is what
 * YouTube Music is built on.
 */

import { parseISODuration, splitTitle } from '../lib/format.ts';
import type { Playlist, Track } from '../types/media.ts';

export interface Thumbnails {
  maxres?: { url: string };
  standard?: { url: string };
  high?: { url: string };
  medium?: { url: string };
  default?: { url: string };
}

export interface VideoSnippet {
  title?: string;
  channelTitle?: string;
  videoOwnerChannelTitle?: string;
  thumbnails?: Thumbnails;
}

export interface VideoItem {
  id: string;
  snippet?: VideoSnippet;
  contentDetails?: { duration?: string };
}

export interface SearchItem {
  id?: { videoId?: string };
  snippet?: VideoSnippet;
}

export interface PlaylistItem {
  snippet?: VideoSnippet;
  contentDetails?: { videoId?: string };
}

export interface PlaylistResource {
  id: string;
  snippet?: { title?: string; thumbnails?: Thumbnails };
  contentDetails?: { itemCount?: number };
}

/** The largest thumbnail YouTube offers for an item. */
export function bestThumb(thumbs: Thumbnails | undefined | null): string | null {
  if (!thumbs) return null;
  const best = thumbs.maxres ?? thumbs.standard ?? thumbs.high ?? thumbs.medium ?? thumbs.default;
  return best?.url ?? null;
}

const trackOf = (
  videoId: string,
  snippet: VideoSnippet,
  duration: number,
  channel: string,
): Track => {
  const { title, artist } = splitTitle(snippet.title ?? '', channel);
  return {
    id: `yt-${videoId}`,
    source: 'youtube',
    videoId,
    title,
    artist,
    album: '',
    duration,
    artworkUrl: bestThumb(snippet.thumbnails),
  };
};

export function videoToTrack(v: VideoItem): Track {
  const sn = v.snippet ?? {};
  return trackOf(v.id, sn, parseISODuration(v.contentDetails?.duration), sn.channelTitle ?? '');
}

export function searchItemToTrack(i: SearchItem, durations: Map<string, number>): Track | null {
  const vid = i.id?.videoId;
  if (!vid) return null;
  const sn = i.snippet ?? {};
  return trackOf(vid, sn, durations.get(vid) ?? 0, sn.channelTitle ?? '');
}

/** Private and deleted entries stay in a playlist but cannot be played. */
export function playlistItemToTrack(i: PlaylistItem, durations: Map<string, number>): Track | null {
  const sn = i.snippet ?? {};
  const vid = i.contentDetails?.videoId;
  if (!vid || sn.title === 'Private video' || sn.title === 'Deleted video') return null;
  return trackOf(vid, sn, durations.get(vid) ?? 0, sn.videoOwnerChannelTitle ?? '');
}

export function playlistResourceToPlaylist(p: PlaylistResource): Playlist {
  const count = p.contentDetails?.itemCount ?? 0;
  return {
    id: p.id,
    source: 'youtube',
    title: p.snippet?.title ?? 'Untitled playlist',
    subtitle: `${count} tracks`,
    count,
    artworkUrl: bestThumb(p.snippet?.thumbnails),
  };
}

/** The synthetic entry for "Your Likes", which is not a real playlist id. */
export const LIKED_PLAYLIST: Playlist = {
  id: 'yt-liked',
  title: 'Liked music',
  subtitle: 'Your likes',
  source: 'youtube',
  special: 'liked',
  icon: 'heart',
};
